
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white/90 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/3b7a7fa1-a2cd-42b2-8fab-278d72f2bccb.png" 
              alt="PERCIC d. o. o. Logo" 
              className="h-10 w-10 object-contain"
            />
            <h1 className="text-xl font-bold text-black">PERCIC d. o. o.</h1>
          </div>
          <div className="flex items-center gap-8">
            <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-black transition-colors cursor-pointer text-sm font-medium whitespace-nowrap">
              Home
            </button>
            <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-black transition-colors cursor-pointer text-sm font-medium whitespace-nowrap">
              About
            </button>
            <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-black transition-colors cursor-pointer text-sm font-medium whitespace-nowrap">
              Products
            </button>
            <button onClick={() => scrollToSection('features')} className="text-gray-700 hover:text-black transition-colors cursor-pointer text-sm font-medium whitespace-nowrap">
              Features
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-black transition-colors cursor-pointer text-sm font-medium whitespace-nowrap">
              Contact
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/20"></div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <h2 className="text-7xl font-extrabold text-white mb-8 tracking-wider leading-tight">
            MODERN iOS APP<br />DEVELOPMENT
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Expert team crafting innovative applications for iOS platform<br />
            Enhancing user experience and mobile device security
          </p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <div className="relative bg-gradient-to-br from-teal-500 to-emerald-600 rounded-3xl p-12 h-96 flex flex-col justify-between">
                <span className="text-white/80 text-sm font-medium tracking-wide">ABOUT US</span>
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-white text-2xl font-light leading-relaxed text-center">
                    "Building the future of mobile applications with passion and expertise"
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-team-line text-3xl text-white"></i>
                  </div>
                  <p className="text-white/90 text-sm">Dedicated iOS Development Team</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-3">
              <span className="text-gray-500 text-sm font-medium tracking-widest uppercase">Who We Are</span>
              <h3 className="text-5xl font-extrabold text-black mt-4 mb-8 leading-tight">
                iOS Development<br />Specialists
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                PERCIC d. o. o. is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                We combine technical excellence with creative innovation to deliver applications that not only meet but exceed user expectations. Our commitment to quality and security ensures that every product we create provides real value to our users.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-gray-500 text-sm font-medium tracking-widest uppercase">Our Solutions</span>
            <h3 className="text-6xl font-extrabold text-black mt-4 leading-tight">
              PRODUCTS THAT<br />MATTER
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-2xl p-10 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-shield-keyhole-line text-5xl text-teal-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-black mb-4">VPN Application</h4>
              <p className="text-gray-600 leading-relaxed">
                Secure and fast VPN solution that protects your privacy and ensures safe browsing on iOS devices. Advanced encryption technology keeps your data protected at all times.
              </p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-10 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-brush-line text-5xl text-emerald-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-black mb-4">Cleaner Application</h4>
              <p className="text-gray-600 leading-relaxed">
                Powerful cleaning tool that optimizes your device performance by removing unnecessary files and managing storage efficiently. Keep your iOS device running smoothly.
              </p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-10 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-smartphone-line text-5xl text-purple-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-black mb-4">Custom Solutions</h4>
              <p className="text-gray-600 leading-relaxed">
                Tailored iOS applications designed to meet specific business needs. We create innovative solutions that drive engagement and deliver exceptional user experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <span className="text-gray-500 text-sm font-medium tracking-widest uppercase">What We Offer</span>
            <h3 className="text-6xl font-extrabold text-black mt-4 leading-tight">
              CORE FEATURES
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-amber-100/50 rounded-2xl p-10">
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <i className="ri-lock-line text-4xl text-black"></i>
              </div>
              <h4 className="text-xl font-bold text-black mb-4">Enhanced Security</h4>
              <p className="text-gray-700 leading-relaxed">
                Industry-leading security protocols and encryption standards to protect user data and ensure privacy across all our applications.
              </p>
            </div>
            <div className="bg-amber-100/50 rounded-2xl p-10">
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <i className="ri-speed-line text-4xl text-black"></i>
              </div>
              <h4 className="text-xl font-bold text-black mb-4">Optimized Performance</h4>
              <p className="text-gray-700 leading-relaxed">
                Lightning-fast applications optimized for iOS devices, ensuring smooth operation and minimal resource consumption for the best user experience.
              </p>
            </div>
            <div className="bg-amber-100/50 rounded-2xl p-10">
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <i className="ri-layout-line text-4xl text-black"></i>
              </div>
              <h4 className="text-xl font-bold text-black mb-4">Intuitive Interface</h4>
              <p className="text-gray-700 leading-relaxed">
                Beautiful and user-friendly interfaces designed following Apple's Human Interface Guidelines for seamless navigation and interaction.
              </p>
            </div>
            <div className="bg-amber-100/50 rounded-2xl p-10">
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <i className="ri-refresh-line text-4xl text-black"></i>
              </div>
              <h4 className="text-xl font-bold text-black mb-4">Regular Updates</h4>
              <p className="text-gray-700 leading-relaxed">
                Continuous improvements and feature updates to keep our applications current with the latest iOS capabilities and user needs.
              </p>
            </div>
            <div className="bg-amber-100/50 rounded-2xl p-10">
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <i className="ri-customer-service-line text-4xl text-black"></i>
              </div>
              <h4 className="text-xl font-bold text-black mb-4">Expert Support</h4>
              <p className="text-gray-700 leading-relaxed">
                Dedicated support team ready to assist users with any questions or issues, ensuring a smooth and satisfying experience with our products.
              </p>
            </div>
            <div className="bg-amber-100/50 rounded-2xl p-10">
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <i className="ri-global-line text-4xl text-black"></i>
              </div>
              <h4 className="text-xl font-bold text-black mb-4">Global Reach</h4>
              <p className="text-gray-700 leading-relaxed">
                Applications available worldwide with multi-language support, bringing our innovative solutions to users across different regions and cultures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-black rounded-3xl p-16">
            <div className="grid grid-cols-1 md:grid-cols-10 gap-12 mb-16">
              <div className="md:col-span-4">
                <h3 className="text-5xl font-light text-white mb-6">GET IN TOUCH</h3>
                <p className="text-gray-400 text-lg leading-relaxed">
                  We're here to answer your questions and discuss how our iOS applications can benefit you.
                </p>
              </div>
              <div className="md:col-span-3">
                <h4 className="text-white text-sm font-medium tracking-widest uppercase mb-6">Location</h4>
                <div className="space-y-3 text-gray-300">
                  <p className="flex items-center gap-3">
                    <i className="ri-map-pin-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    <span>Address: Brdo 9</span>
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="ri-building-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    <span>City: Icici</span>
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="ri-mail-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    <span>Zip: 51414</span>
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="ri-earth-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    <span>Country: Croatia</span>
                  </p>
                </div>
              </div>
              <div className="md:col-span-3">
                <h4 className="text-white text-sm font-medium tracking-widest uppercase mb-6">Contact</h4>
                <div className="space-y-4 text-gray-300">
                  <p className="flex items-center gap-3">
                    <i className="ri-mail-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    <span>Email: help@percicdoo.net</span>
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="ri-phone-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    <span>Number: +1 4634003089</span>
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-gray-400 text-sm">
                © 2025 PERCIC d. o. o. All rights reserved.
              </div>
              <div className="text-gray-400 text-sm">
                <span className="cursor-pointer hover:text-white transition-colors whitespace-nowrap">
                  <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    Powered by Readdy
                  </a>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
